import { type IBrand } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Col, Form, Input, Row, Select, Space } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getFilterOption, getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { brandsLoadAll } from '../../reducers/brand/reducer';
import { brandSet } from '../../reducers/product/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import FormSubmitFooter from '../form-submit-footer';

interface IForm {
  brand: IBrand;
  mpn: string;
  gtin: string;
}

function BrandAndIdentification(): React.ReactElement {
  const dispatch = useAppDispatch();
  const brands = useSelector((s: RootState) => s.Brand.brands);
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productBrand = useSelector((s: RootState) => s.Product.brand);
  const productMPN = useSelector((s: RootState) => s.Product.mpn);
  const productGTIN = useSelector((s: RootState) => s.Product.gtin);

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ brand, mpn, gtin }: IForm): void => {
      dispatch(brandSet(productUUID, brand, mpn, gtin));
    },
    [dispatch, productUUID],
  );

  useEffect((): void => {
    dispatch(brandsLoadAll());
  }, [dispatch]);

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          brand: productBrand,
          mpn: productMPN,
          gtin: productGTIN,
        }}
      >
        <Row gutter={SPACING.lg}>
          <Col xs={24} md={8}>
            <Form.Item
              name={['brand', 'uuid']}
              rules={[
                {
                  required: true,
                },
              ]}
              label="Brand"
            >
              <Select
                placeholder="Select brand"
                disabled={productInProcess}
                showSearch={{
                  filterOption: getFilterOption,
                  filterSort: getFilterSort,
                }}
                options={brands?.map((brand: IBrand) => ({
                  value: brand.uuid,
                  label: brand.name,
                }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="MPN">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="mpn"
                  noStyle
                  rules={[
                    {
                      required: true,
                    },
                    {
                      type: 'string',
                      min: 2,
                    },
                  ]}
                >
                  <Input placeholder="MPN" disabled={productInProcess} />
                </Form.Item>
                <CopyToClipboard
                  text={productMPN || ''}
                  size="small"
                  type="link"
                />
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="GTIN / EAN">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="gtin"
                  noStyle
                  rules={[
                    {
                      required: false,
                    },
                    {
                      type: 'string',
                      min: 2,
                    },
                  ]}
                >
                  <Input placeholder="GTIN / EAN" disabled={productInProcess} />
                </Form.Item>
                <CopyToClipboard
                  text={productGTIN || ''}
                  size="small"
                  type="link"
                />
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <FormSubmitFooter
              inProcess={productInProcess}
              updatedAt={productUpdatedAt}
            />
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default React.memo(BrandAndIdentification);
